/** biome-ignore-all lint/suspicious/noExplicitAny: TODO: fix */
import { createProvider } from '@nvision/services/data-provider';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import type { AccessorialDetailsParams, AccessorialsFilters, TariffDetailsParams } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';
import * as api from './api';
import * as mock from './mock';
import { normalizeAccessorials, normalizeTariff, normalizeTariffPricing } from './normalize';
import { normalizeAccessorialsFiltersUpdate } from './search-accessorials';

export const fetchTariff = createProvider(api.fetchTariff)
  .andThen(normalizeTariff)
  .andCatch((error) => {
    if (error instanceof Error && error.name === 'AbortError') {
      logger.warn('Fetch tariff aborted');
    } else {
      logger.error('Fetch tariff failed:', error);
    }
    throw error;
  });

export const tariffQueryOptions = (tariffKey: number) =>
  queryOptions({
    queryFn: ({ signal }) => fetchTariff({ tariffKey }, { signal }),
    queryKey: CACHE_KEYS.tariff(tariffKey),
    // biome-ignore lint/style/noMagicNumbers: 1 hour
    staleTime: 1000 * 60 * 60,
  });

export const fetchTariffPricing = createProvider(api.fetchTariffPricing).andThen(normalizeTariffPricing);

export const tariffPricingQueryOptions = (tariffKey: number) =>
  queryOptions({
    queryFn: ({ signal }) => fetchTariffPricing({ tariffKey }, { signal }),
    queryKey: CACHE_KEYS.tariffPricing(tariffKey),
    // biome-ignore lint/style/noMagicNumbers: 1 hour
    staleTime: 1000 * 60 * 60,
  });

export const fetchTariffDetails = createProvider(api.fetchTariffDetails)
  .andThen((res) => res)
  .andCatch((error) => {
    if (error instanceof Error && error.name === 'AbortError') {
      logger.warn('Fetch tariff details aborted');
    } else {
      logger.error('Fetch tariff details failed:', error);
    }
    throw error;
  });

export const tariffDetailsOptions = (params: TariffDetailsParams) =>
  queryOptions({
    queryFn: ({ signal }) => fetchTariffDetails(params, { signal }),
    queryKey: CACHE_KEYS.tariffDetails(params),
    retry: false,
    // biome-ignore lint/style/noMagicNumbers: 1 hour
    staleTime: 1000 * 60 * 60,
  });

export const fetchAccessorialDetails = createProvider(api.fetchAccessorialDetails)
  .andMock(mock.fetchAccessorialDetails)
  .andThen((res) => res);

export const accessorialDetailsQueryOptions = (params: AccessorialDetailsParams) =>
  queryOptions({
    queryFn: ({ signal }) => fetchAccessorialDetails(params, { signal }),
    queryKey: CACHE_KEYS.accessorialDetails(params),
    // biome-ignore lint/style/noMagicNumbers: 1 hour
    staleTime: 1000 * 60 * 60,
  });

export const fetchAccessorials = createProvider(
  (
    filters: AccessorialsFilters,
    { customerKey, skip, take }: { customerKey: number; skip?: number; take?: number },
    { signal }: { signal?: AbortController['signal'] } = {},
  ) => api.fetchAccessorials({ ...normalizeAccessorialsFiltersUpdate(filters, customerKey), skip, take }, { signal }),
)
  .andThen(normalizeAccessorials)
  .andCatch((error) => {
    if (error instanceof Error && error.name === 'AbortError') {
      logger.warn('Fetch accessorials aborted');
    } else {
      logger.error('Fetch accessorials failed:', error);
    }
    throw error;
  });

export const ACCESSORIALS_PAGE_SIZE = 10;

export const accessorialsInfiniteQueryOptions = (
  filters: AccessorialsFilters = {},
  { customerKey, enabled = true }: { customerKey: number; enabled?: boolean },
) =>
  // biome-ignore assist/source/useSortedKeys: TODO: wait for tanstack query update
  infiniteQueryOptions({
    enabled,
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) =>
      fetchAccessorials(filters, { customerKey, skip: pageParam, take: ACCESSORIALS_PAGE_SIZE }, { signal }),
    queryKey: CACHE_KEYS.accessorials(filters, customerKey),
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((count, page) => count + page.items.length, 0);

      return loadedCount < lastPage.totalCount ? loadedCount : undefined;
    },
    select: (data) => ({
      items: data.pages.flatMap((page) => page.items ?? []),
      totalCount: data.pages[0]?.totalCount ?? 0,
    }),
    // biome-ignore lint/style/noMagicNumbers: 1 hour
    staleTime: 1000 * 60 * 60,
  });

export const fetchSortingOptions = createProvider(api.fetchSortingOptions).andThen((res) => res);

export const sortingOptionsQueryOptions = () =>
  queryOptions({
    queryFn: ({ signal }) => fetchSortingOptions({ signal }),
    queryKey: CACHE_KEYS.sortingOptions(),
    // biome-ignore lint/style/noMagicNumbers: 1 hour
    staleTime: 1000 * 60 * 60,
  });
